<template>
  <b-sidebar :id="sidebarFields" class="sidebar-fields" ref="sidebar_fields" title="Настройка колонок" right shadow backdrop>
    <div class="px-1 py-1">
      <b-list-group flush bg-variant="light">
        <draggable v-model="localFields" handle=".drag-handle">
          <div
            v-for="field in localFields"
            :key="field.id"
          >
            <b-list-group-item
              v-if="field.isSeparator"
              variant="secondary"
              class="font-weight-bold text-small text-muted text-uppercase px-2 py-2 mt-1 mb-1"
            >
              {{ field.label }}
            </b-list-group-item>

            <b-list-group-item
              v-else
              bg-variant="light"
              class="d-flex justify-content-between align-items-center text-small px-2 py-2"
            >
              <div class="d-inline-flex align-items-center">
                    <span class="drag-handle text-primary mr-3">
                      <font-awesome-icon icon="bars"/>
                    </span>
                {{ field.label }}
                <span v-if="configureRequired && field.required && field.visible" class="text-danger font-weight-bold ml-2">*</span>
              </div>
              <b-form-checkbox
                :disabled="visibleFields.length === 1 && field.visible"
                v-model="field.visible"
                stack
                switch
                class="ml-auto"
              />
              <b-form-checkbox
                v-if="configureRequired"
                v-model="field.required"
                :disabled="!field.visible"
                class="ml-1"
              />
              <span class="sr-only"></span>
            </b-list-group-item>
          </div>
        </draggable>
      </b-list-group>
    </div>

    <template #footer>
      <b-row class="mx-1 my-2">
        <b-button size="sm" variant="danger" class="mr-1" @click="resetColumns">
          <font-awesome-icon icon="trash-restore" class="mr-1"/>
          Сброс
        </b-button>
        <b-button v-if="checkAll" size="sm" variant="primary" @click="checkAllColumns(false)">
          <font-awesome-icon :icon="['far', 'square']" class="mr-1"/>
          Снять все
        </b-button>
        <b-button v-else size="sm" variant="info" @click="checkAllColumns(true)">
          <font-awesome-icon icon="check-square" class="mr-1"/>
          Выбрать все
        </b-button>
        <b-button size="sm" variant="success" class="ml-1" @click="saveColumns(false)">
          <font-awesome-icon icon="save" class="mr-1" />
          Сохранить
        </b-button>
      </b-row>
    </template>
  </b-sidebar>
</template>

<script>
import draggable from "vuedraggable";

export default {
  name: 'DynamicTables',
  components: { draggable },
  props: {
    storageKey: { type: String, required: true },
    sidebarFields: { type: String, required: true },
    fieldsModel: { type: Array, required: true },
    fieldKey: { type: String, required: true },
    configureRequired: { type: Boolean, default: false }
  },
  data() {
    return {
      localFields: [],
      checkAll: true,
    };
  },
  async created() {
    this.initColumns();
  },
  computed: {
    visibleFields() {
      return this.localFields.filter(field => field.visible && !field.isSeparator);
    },
  },
  watch: {
    fieldsModel: {
      handler(newFields, oldFields) {
        const hasNewKeys = newFields.some(f => !oldFields.some(of => of.key === f.key));

        if (newFields.length !== oldFields.length || hasNewKeys) {
          this.initColumns();
        }
      },
      deep: true
    },
    localFields(newFields) {
      newFields.forEach(field => {
        if (this.configureRequired && !field.visible) {
          field.required = false;
        }
      });
      this.$emit('update:fieldsModel', newFields);
    }, deep: true
  },
  methods: {
    checkAllColumns(flag) {
      this.localFields.forEach(item => {
        if (item.isSeparator) return;

        item.visible = flag || item.key === this.fieldKey;
      })
      this.checkAll = flag;
    },
    getStoredColumns() {
      const storedFields = localStorage.getItem(this.storageKey);
      return storedFields ? JSON.parse(storedFields) : null;
    },
    mergeColumns(incomingFields) {
      const storedSettings = this.getStoredColumns();

      if (!storedSettings) {
        return [...incomingFields];
      }

      const mergedFields = storedSettings.map(storedField => {
        const baseField = incomingFields.find(f => f.key === storedField.key);

        if (baseField) {
          const storedRequired = storedField.required !== undefined && storedField.required !== null;
          const baseRequired = baseField.required !== undefined && baseField.required !== null;

          return {
            ...baseField,
            visible: storedField.visible,
            required: storedRequired ? storedField.required : (baseRequired ? baseField.required : false)
          };
        }
        return null;
      }).filter(Boolean);

      const storedKeys = new Set(mergedFields.map(f => f.key));
      const newFields = incomingFields
        .filter(f => !storedKeys.has(f.key))
        .map(f => ({
          ...f,
          required: f.required || false
        }));

      return [...mergedFields, ...newFields];
    },
    initColumns() {
      this.localFields = this.mergeColumns(this.fieldsModel);
      this.checkAll = this.localFields.every(field => field.visible);
    },
    saveColumns() {
      localStorage.setItem(this.storageKey, JSON.stringify(this.localFields));
      this.$bvToast.toast("Успешное сохранение колонок.", {
        title: 'Сохранено',
        variant: 'success',
        autoHideDelay: 3000,
        solid: true
      });
      this.$refs.sidebar_fields.hide();
    },
    async resetColumns() {
      this.$refs.sidebar_fields.hide();
      const isConfirmed = await this.$bvModal.msgBoxConfirm('Вы хотите вернуть стандартные настройки? (Это также обновит текущую страницу).', {
        title: 'Сброс настроек',
        size: 'md',
        buttonSize: 'sm',
        headerBgVariant: 'danger',
        okVariant: 'danger',
        okTitle: 'Сбросить',
        cancelTitle: 'Отмена',
        footerClass: 'p-1',
        centered: true
      });

      if (!isConfirmed) return;

      localStorage.removeItem(this.storageKey);
      this.initColumns();

      this.$bvToast.toast("Настройки таблицы сброшены.", {
        title: 'Успешно',
        variant: 'warning',
        autoHideDelay: 2000,
        solid: true
      });
      setTimeout(() => {
        location.reload();
      }, 1111);
    }
  },
}
</script>

<style>
.drag-handle {
  cursor: grab;
}
.sidebar-fields {
  z-index: 1060 !important;
}
</style>



// WEBPACK FOOTER //
// src/utilites/commonUi/DynamicTables.vue